import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { useMarkRead, useNotifications, useUnreadCount } from "@/api/queries";
import { useNotificationsStream } from "@/live/useNotificationsStream";
import { initials } from "@/lib/format";
import { Logo, SuperadminBadge } from "./index";
import { isSuperadmin } from "@/lib/roles";

/** Закрывает выпадающее меню по клику вне его и по Escape */
function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    // pointerdown одинаково ловит мышь и касание (Android, iOS)
    document.addEventListener("pointerdown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);
  return ref;
}

/** «2 ч назад», «вчера», «3 дн назад» */
function agoText(iso: string, now: Date = new Date()): string {
  const ms = now.getTime() - new Date(iso).getTime();
  const minutes = Math.max(0, Math.floor(ms / 60000));
  if (minutes < 1) return "только что";
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "вчера";
  return `${days} дн назад`;
}

function Bell() {
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  const unread = useUnreadCount();
  const list = useNotifications(5);
  const markRead = useMarkRead();
  const nav = useNavigate();
  const count = unread.data?.count ?? 0;
  const items = list.data?.pages[0]?.items ?? [];
  const openItem = (id: string, link: string | null, readAt: string | null) => {
    if (!readAt) markRead.mutate([id]);
    setOpen(false);
    if (link) nav(link);
  };
  return (
    <div className="nav__dropdown" ref={ref}>
      <button
        type="button"
        className="nav__icon-btn"
        aria-label={count ? `Уведомления, ${count} новых` : "Уведомления"}
        aria-expanded={open}
        onClick={() => {
          setOpen((o) => !o);
          if (!open) list.refetch();
        }}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {count > 0 && <span className="nav__badge">{count > 99 ? "99+" : count}</span>}
      </button>
      {open && (
        <div className="menu menu--wide" role="menu">
          <div className="menu__head">
            <span>Уведомления</span>
            {count > 0 && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => markRead.mutate(null)}
              >
                Прочитать все
              </button>
            )}
          </div>
          {items.length === 0 && <div className="menu__empty">Пока ничего нового</div>}
          {items.map((n) => (
            <button
              key={n.id}
              type="button"
              role="menuitem"
              className={`notice ${n.read_at ? "" : "notice--unread"}`}
              onClick={() => openItem(n.id, n.link, n.read_at)}
            >
              <span className="notice__dot" aria-hidden="true" />
              <span className="notice__body">
                <span className="notice__title">{n.title}</span>
                <span className="notice__text">{n.text}</span>
                <span className="notice__when">{agoText(n.created_at)}</span>
              </span>
            </button>
          ))}
          <Link to="/notifications" className="menu__foot" onClick={() => setOpen(false)}>
            Все уведомления
          </Link>
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useDismiss(open, () => setOpen(false));
  if (!user) return null;
  return (
    <div className="nav__dropdown" ref={ref}>
      <button
        type="button"
        className="nav__user"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="avatar-wrap">
          <span className="avatar">{initials(user.display_name)}</span>
          {isSuperadmin(user) && <SuperadminBadge compact />}
        </span>
        <span className="nav__user-name">{user.display_name}</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          style={{ opacity: 0.7 }}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="menu" role="menu">
          <div className="menu__head menu__head--user">
            <span>{user.display_name}</span>
            <span className="small muted">{user.email}</span>
            {isSuperadmin(user) && <SuperadminBadge />}
          </div>
          <Link to="/profile" role="menuitem" className="menu__item" onClick={() => setOpen(false)}>
            Профиль и история
          </Link>
          <button
            type="button"
            role="menuitem"
            className="menu__item menu__item--danger"
            onClick={() => {
              setOpen(false);
              logout().then(() => nav("/login"));
            }}
          >
            Выйти
          </button>
        </div>
      )}
    </div>
  );
}

const TAB_ICONS: Record<string, ReactNode> = {
  teams: <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7l8-4z" />,
  competitions: (
    <>
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4z" />
      <path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3" />
    </>
  ),
  players: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
      <path d="M16 4.5a3.5 3.5 0 0 1 0 7M18 14c2.2.6 3.5 2.8 3.5 6" />
    </>
  ),
  matches: (
    <>
      <rect x="3" y="5" width="18" height="16" rx="3" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </>
  ),
  rules: (
    <>
      <path d="M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3V4z" />
      <path d="M5 17a3 3 0 0 1 3-3h11M9 8h6" />
    </>
  ),
};

/** Нижняя панель разделов на телефоне (на компьютере скрыта стилями) */
function TabBar({ teamsActive }: { teamsActive: boolean }) {
  const tabs: [string, string, string][] = [
    ["teams", "/", "Команды"],
    ["competitions", "/competitions", "Соревнования"],
    ["players", "/players", "Игроки"],
    ["matches", "/matches", "Матчи"],
    ["rules", "/rules", "Правила"],
  ];
  return (
    <nav className="tabbar" aria-label="Разделы">
      {tabs.map(([key, to, label]) => (
        <NavLink
          key={key}
          to={to}
          end={to === "/"}
          className={({ isActive }) =>
            `tabbar__item ${(key === "teams" ? teamsActive : isActive) ? "tabbar__item--active" : ""}`
          }
        >
          <span className="tabbar__icon">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {TAB_ICONS[key]}
            </svg>
          </span>
          <span className="tabbar__label">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const { user } = useAuth();
  const loc = useLocation();
  useNotificationsStream(!!user);
  const cls = ({ isActive }: { isActive: boolean }) =>
    `nav__link ${isActive ? "nav__link--active" : ""}`;
  // «Мои команды» активна на главной и на всех экранах команды
  const teamsActive = loc.pathname === "/" || loc.pathname.startsWith("/entries");
  // в редакторе состава внизу свои кнопки «Автозаполнить» и «Сохранить» вместо разделов
  const editor = /^\/entries\/[^/]+\/(edit|gameweeks\/[^/]+)\/?$/.test(loc.pathname);
  const tabs = !!user && !editor;
  return (
    <div className={`app ${tabs ? "app--tabs" : ""} ${editor ? "app--editor" : ""}`}>
      <nav className="nav" aria-label="Основная навигация">
        <Link to="/" className="nav__brand">
          <Logo /> <span>Picked</span>
        </Link>
        <div className="nav__links">
          <NavLink
            to="/"
            end
            className={() => `nav__link ${teamsActive ? "nav__link--active" : ""}`}
          >
            Мои команды
          </NavLink>
          <NavLink to="/competitions" className={cls}>
            Соревнования
          </NavLink>
          <NavLink to="/players" className={cls}>
            Игроки
          </NavLink>
          <NavLink to="/matches" className={cls}>
            Матчи
          </NavLink>
          <NavLink to="/rules" className={cls}>
            Правила
          </NavLink>
        </div>
        {user && (
          <div className="nav__right">
            <Bell />
            <UserMenu />
          </div>
        )}
      </nav>
      {children ?? <Outlet />}
      {tabs && <TabBar teamsActive={teamsActive} />}
    </div>
  );
}

export function AuthLayout({ children }: { children?: ReactNode }) {
  return (
    <div className="auth-layout">
      <div className="auth-layout__top">
        <Link to="/" className="nav__brand">
          <Logo /> <span>Picked</span>
        </Link>
      </div>
      <div className="auth-layout__body">{children ?? <Outlet />}</div>
    </div>
  );
}
